import { message, Spin } from 'antd';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import {
  useActivateFlow,
  useCreateFlow,
  useEventListForCreateMetric,
  useGetFlowList,
  usePutActivateFlow,
  useUpdateFlow,
} from '@src/emp-3.0/api/queryHooks';
import { IMetric, EStatus } from '@src/emp-3.0/types';

import { EFormMode } from '@src/emp-3.0/types/common';
import { cloneDeep, isEqual } from 'lodash';
import FlowForm from '@src/emp-3.0/components/Metrics/Flow/FlowForm';
import BuilderWrapper from 'pages/workflow/components/BuilderWrapper';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { FLOW_FORM_CONFIG, PATTERN_FORM_CONFIG } from '@src/emp-3.0/constants/metrics';
import { formValidator } from '@src/pages/workflow/utils';
import useErrorFields from '@src/pages/workflow/hooks/useErrorFields';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { Layout, ContentWrapper } from '@src/pages/workflow/components/FlowBuilder/styled';
import { Footer } from './components';
import FormValidatorContext from '../../FormValidatorContext';
import { useBuildFlowFormData } from '@src/emp-3.0/hooks';
import { MappedEventTypes } from '@src/common/types/empTypes';
import { useIsPatternMode } from '@src/pages/metric-templates/utils';

const flowListDefault = [];
const FlowBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { buildFlowFormData } = useBuildFlowFormData();
  const hasCreatePermission = useCreatePermissions();
  const { flowId } = useParams<any>();
  const [flowData, setFlowData] = useState<any>(null);
  const [flowFormData, setFlowFormData] = useState<any>(null);

  const [hasMetadataChanged, setMetadataChanged] = useState<boolean>(false);
  const [keepHistoricalData, setKeepHistoricalData] = useState<boolean>(true);
  const [serverSideError, setServerSideError] = useState<string>('');

  // Get list and create/update flow
  const { isLoading: isFlowEventListLoading } = useEventListForCreateMetric();
  const { data: flowList = flowListDefault, isLoading: isGetFlowListLoading } = useGetFlowList();
  const { mutateAsync: create, isLoading: createLoading } = useCreateFlow();
  const { mutateAsync: update, isLoading: updateLoading } = useUpdateFlow();
  const { mutateAsync: activate, isLoading: activateLoading } = useActivateFlow();
  const { mutateAsync: putActivate, isLoading: putActivateLoading } = usePutActivateFlow();

  const { errorFields, setErrorFields, resetErrorField } = useErrorFields();
  const { setFormMode, hasChanged, setHasChanged } = useWorkflowStore();
  const {
    formInstance,
    initialEvent,
    setInitialEvent,
    completeEvent,
    setCompleteEvent,
    errorEvents,
    setActiveTool,
    setActiveSectionKey,
    setDeploymentStatus,
    resetAll,
    selectedFlow,
    setSelectedFlow,
  } = useFlowStore();

  const isCreateNewFlow = flowId === 'new';

  const { isPatternMode } = useIsPatternMode();
  useEffect(() => {
    setActiveTool(null);
  }, []);

  useEffect(() => {
    setHasChanged(false);
    if (isCreateNewFlow) {
      resetAll();
      const params = new URLSearchParams(location.search);
      const from = params.get('from');
      if (from) {
        try {
          const initialData = JSON.parse(localStorage.getItem(from) as string);
          const { start_event = {}, end_event = {} } = initialData;
          setInitialEvent(start_event as MappedEventTypes);
          setCompleteEvent(end_event as MappedEventTypes);
        } catch {
          localStorage.removeItem(from);
          navigate(`/workflow/flow/new`);
        }
      }
      setFormMode(EFormMode.NEW);
    } else {
      setFormMode(EFormMode.EDIT);
    }

    return () => {
      setFormMode(EFormMode.VIEW);
    };
  }, [flowId, isCreateNewFlow, navigate, setCompleteEvent, setInitialEvent]);

  useEffect(() => {
    let flowData: any = null;
    if (flowId !== 'new') {
      // get flow data from flow list
      flowData = cloneDeep(flowList.find((x) => x.id === Number(flowId)));
    }
    if (isCreateNewFlow && selectedFlow) {
      // If creating a new flow from a suggestion, use the selected flow data
      flowData = cloneDeep(selectedFlow);
      setActiveSectionKey(['1', '2', '3']);
    }

    // reset keep historic data
    // 2025.04.03 flow group discussion:
    // Before deployment, during the editing phase, the system should remember the customer’s last selection.
    // After deployment, the option should automatically return to the default.
    if (flowData && flowData.status === EStatus.UNDEPLOYED) {
      setKeepHistoricalData(flowData.keep_historical);
    } else {
      setKeepHistoricalData(true);
    }
    const flowFormData = buildFlowFormData(flowData);
    setFlowData(flowData);
    setFlowFormData(flowFormData);
  }, [buildFlowFormData, flowId, flowList, selectedFlow]);

  const createFlow = useCallback(
    async (flow) => {
      await create(
        { ...flow },
        {
          onSuccess: (data) => {
            message.success('Build new flow success!');
            const url = location.pathname.replace('new', data?.data.id);
            navigate(url, { replace: true });
          },
          onError: (error: any) => {
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    },
    [create, setFormMode],
  );

  const updateFlow = useCallback(
    async (flow) => {
      const { id } = flowData as IMetric;
      await update(
        { flow_id: id, payload: { ...flow } },
        {
          onSuccess: () => {
            message.success('Update flow success!');
          },
          onError: (error: any) => {
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    },
    [flowData, update],
  );

  const validateForm = useCallback(async () => {
    try {
      // reset error
      setErrorFields({});
      setServerSideError('');
      // required check for flow form
      formInstance.validateFields([['definition', 'propagative_dimensions']]);
      const shouldOpenedFlowFormSections: any[] = [];
      const formConfig = isPatternMode ? PATTERN_FORM_CONFIG : FLOW_FORM_CONFIG;
      const values = await formValidator(formInstance, formConfig, false).catch((e) => {
        const errorKeys = Object.keys(e.errorFields) || [];
        const errorEventError = errorKeys.some((key) =>
          key.startsWith('definition.events.error_event'),
        );
        const eventError = errorKeys.some((key) => key.startsWith('definition.events'));
        const flowWidgetError = errorKeys.some(
          (key) =>
            key.startsWith('definition.settings') ||
            key.startsWith('definition.events.without_event'),
        );
        const sqlError = errorKeys.some((key) => key.startsWith('definition.flow_sql_definition'));
        if (eventError) {
          if (errorEventError) {
            shouldOpenedFlowFormSections.push('2');
          }
          shouldOpenedFlowFormSections.push('1');
        }
        if (flowWidgetError) {
          shouldOpenedFlowFormSections.push('3');
        }
        if (sqlError) {
          shouldOpenedFlowFormSections.push('1');
        }
        setErrorFields(e.errorFields);
        return false;
      });
      // first check if there is any error in the form
      if (shouldOpenedFlowFormSections.length > 0) {
        setActiveSectionKey(shouldOpenedFlowFormSections);
        return false;
      }

      // isEditing check for event rule builder
      if (initialEvent || completeEvent || errorEvents.some((i) => typeof i === 'object')) {
        if (initialEvent || completeEvent) {
          shouldOpenedFlowFormSections.push('1');
        }
        if (errorEvents.some((i) => typeof i === 'object')) {
          shouldOpenedFlowFormSections.push('2');
        }
        setErrorFields({ event: 'Editing' });
      }
      // second check if there is any error in the event rule builder
      if (shouldOpenedFlowFormSections.length > 0) {
        setActiveSectionKey(shouldOpenedFlowFormSections);
        return false;
      }

      // for keep historical data
      values['keep_historical'] = keepHistoricalData;

      return values;
    } catch (err) {
      console.log('validate form error', err);
    }
  }, [
    completeEvent,
    errorEvents,
    formInstance,
    initialEvent,
    setActiveSectionKey,
    setErrorFields,
    keepHistoricalData,
    isPatternMode,
  ]);

  const handleCancel = () => {
    setSelectedFlow(undefined);
    if (flowId === 'new') {
      navigate(`/metric-builder`);
    } else if (flowData?.flow_definition_type === 'sql') {
      navigate(`/metric-builder/pattern/${flowId}`);
    } else {
      navigate(`/metric-builder/flow/${flowId}`);
    }
  };

  const handleSave = useCallback(async () => {
    try {
      const values = await validateForm();
      // for keep historical data
      values['keep_historical'] = keepHistoricalData;

      // Remove without_event key if it's null or empty
      if (
        values?.definition?.events?.without_event === null ||
        values?.definition?.events?.without_event === undefined
      ) {
        delete values?.definition?.events?.without_event;
      }

      if (isCreateNewFlow) {
        await createFlow(values);
      } else {
        await updateFlow(values);
      }
    } catch (e) {
      console.log('create/update flow error: ', e);
    }
  }, [validateForm, keepHistoricalData, isCreateNewFlow, createFlow, updateFlow]);

  const handleDeploy = useCallback(async () => {
    const values = await validateForm();
    if (!values) {
      return;
    }

    // Remove without_event key if it's null or empty
    if (
      values?.definition?.events?.without_event === null ||
      values?.definition?.events?.without_event === undefined
    ) {
      delete values?.definition?.events?.without_event;
    }

    setActiveTool('deployment');
    setDeploymentStatus('inprogress');

    if (flowId === 'new') {
      await activate(values, {
        onSuccess: (data) => {
          const url = location.pathname.replace('new', data?.data.id);
          navigate(url, { replace: true });
          setDeploymentStatus('success');
        },
        onError: (error: any) => {
          setDeploymentStatus('failed');
          const errorMsg = error?.response?.data?.msg || 'unknown error';
          setServerSideError(errorMsg);
        },
      });
    } else {
      await putActivate(
        { flow_id: Number(flowId), payload: values },
        {
          onSuccess: () => {
            setDeploymentStatus('success');
          },
          onError: (error: any) => {
            setDeploymentStatus('failed');
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    }
  }, [flowId, initialEvent, completeEvent, errorEvents, validateForm]);

  const errorMsg = useMemo(() => {
    if (Object.values(errorFields).some((x) => x === 'Required')) {
      return '* Required fields in red are missing.';
    } else if (Object.values(errorFields).some((x) => x === 'Editing')) {
      return 'Still has unsaved event changes.';
    } else {
      return serverSideError;
    }
  }, [errorFields, serverSideError]);

  const isLoading = isGetFlowListLoading || isFlowEventListLoading;
  const saveLoading = createLoading || updateLoading;
  const deployLoading = activateLoading || putActivateLoading;
  const footerButtonLoading = saveLoading || deployLoading;
  const actionButtonProps: any = {
    showConfirm: (!isCreateNewFlow && hasMetadataChanged) || !keepHistoricalData,
    disabled: !hasCreatePermission || footerButtonLoading,
    confirmProps: {
      cancelButtonProps: { disabled: deployLoading || saveLoading },
    },
  };

  return (
    <BuilderWrapper
      error={errorMsg}
      footer={() => {
        return (
          <Footer
            type={'flow'}
            showKeepHistoricalData={!isCreateNewFlow}
            checkboxProps={{
              checked: keepHistoricalData,
              disabled: footerButtonLoading,
              onChange: (e) => {
                setKeepHistoricalData(e.target.checked);
              },
            }}
            cancelButtonProps={{
              showConfirm: hasChanged,
              disabled: footerButtonLoading,
              onClick: handleCancel,
            }}
            saveButtonProps={{
              onClick: handleSave,
              loading: saveLoading,
              ...actionButtonProps,
            }}
            deployButtonProps={{
              onClick: handleDeploy,
              loading: deployLoading,
              ...actionButtonProps,
            }}
          />
        );
      }}
    >
      <Spin spinning={saveLoading || deployLoading} indicator={undefined}>
        <Layout>
          <ContentWrapper as={'div'}>
            {isLoading ? (
              <Spin spinning={isLoading} />
            ) : (
              <div>
                <FormValidatorContext.Provider value={{ enableRequired: false, errorFields }}>
                  <FlowForm
                    mode={isCreateNewFlow ? 'new' : 'edit'}
                    flow={flowFormData}
                    formProps={{
                      onValuesChange: (changedValues, values) => {
                        const newValues = buildFlowFormData(values);
                        const originValues = buildFlowFormData(flowData);
                        const isChange = !isEqual(originValues, newValues);
                        const metadataChanged = !isEqual(
                          newValues.definition,
                          originValues.definition,
                        );
                        setMetadataChanged(metadataChanged);
                        setHasChanged(isChange);

                        resetErrorField(changedValues);
                        setServerSideError('');
                      },
                    }}
                    resetErrorField={resetErrorField}
                  />
                </FormValidatorContext.Provider>
              </div>
            )}
          </ContentWrapper>
        </Layout>
      </Spin>
    </BuilderWrapper>
  );
};

export default FlowBuilder;
